import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Stepper, Step, StepLabel, Grid } from '@mui/material';
import useFetch from 'use-http';
import { useCommandeContext, changeValeur } from "../contexts/CommandeContext";
import ServiceValidation from '../services/ServiceValidation';
import EtapeProduitDetails from './EtapeProduitDetails';
import EtapeAdresseCreneau from './EtapeAdresseCreneau';
import EtapeDevisPaiement from './EtapeDevisPaiement';
import EtapeConfirmation from './EtapeConfirmation';
import PaiementStripe from './PaiementStripe';
import EtapeErreur from './EtapeErreur';
import ButtonNav from './button/ButtonNav';
import Loader from './loader/Loader';
import Garanties from './static/Garanties';
import ModePaiement from './static/ModePaiement';
import Cookies from 'js-cookie';

export default function Commande({ finalize = false }) {

	const steps = ["Détails de l'intervention", "Créneau d'intervention", "Récapitulatif et Paiement"];

	let defaultValidation = {
		isValid: true,
		messageErreur: null
	}

	let { prestationKey, produitKey } = useParams();

	const stepperRef = useRef();

	const [activeStep, setActiveStep] = useState(finalize ? 3 : 0);
	const [validationProduitDetails, setValidationProduitDetails] = useState(defaultValidation);

	const [validationAdresseCreneau, setValidationAdresseCreneau] = useState(defaultValidation);
	const [displayCreneaux, setDisplayCreneaux] = useState(false);
	const [creneauxList, setCreneauxList] = useState(null);
	const [creneauxBestPrice, setCreneauxBestPrice] = useState(null);
	const [creneauxBestDate, setCreneauxBestDate] = useState(null);
	const [isSortedTarifs, setIsSortedTarifs] = useState(true);
	const [isSortedDates, setIsSortedDates] = useState(false);

	const [validationDevisPaiement, setValidationDevisPaiement] = useState(defaultValidation);
	const [displayPaiement, setDisplayPaiement] = useState(false);
	const [clientSecret, setClientSecret] = useState('');

	const [loadingText, setLoadingText] = useState('');
	const [loadingTextComp, setLoadingTextComp] = useState('');

	// Initialisation de l'accés au backend
	const { post, response, loading, error } = useFetch(process.env.REACT_APP_BACKEND_API_URL);

	// Recupération de la commande et du dispatcher à partir du CommandeContext
	const { commande, dispatch } = useCommandeContext();

	const handleNext = () => {

		let validation = ServiceValidation.validateEtapeProduitDetails(commande);
		setValidationProduitDetails(validation);

		if (validation.isValid) {

			if (commande.initState) {
				setDisplayCreneaux(false);
				setCreneauxList(null);
				setCreneauxBestDate(null);
				setCreneauxBestPrice(null);
				dispatch(changeValeur('initState', false));
			}

			setActiveStep(1);
			stepperRef.current.scrollIntoView();
		}
	};

	const handleBack = () => {
		setActiveStep(activeStep - 1);
		stepperRef.current.scrollIntoView();
	};

	const handleSearch = async () => {

		setDisplayCreneaux(false);

		let validation = ServiceValidation.validateEtapeAdresseCreneau(commande);
		setValidationAdresseCreneau(validation);

		if (validation.isValid) {

			setLoadingText("Recherche des créneaux disponibles ...");
			setLoadingTextComp("La recherche peut durer jusqu'à 30 secondes, merci de patienter.");

			let quantitesUnitesTravail = {};
			// Parcours des quantites d'unités de travail enregistrées 
			// afin d'initialiser l'objet à passer dans les paramètres de la recherche
			Object.entries(commande).forEach(function([key, value]) {
				if (key.startsWith('ut-') && (value !== '' && value != null && value > 0)) {
					quantitesUnitesTravail[key] = value;
				}
			});

			let quantitesOptions = {};
			// Parcours des quantites d'options enregistrées 
			// afin d'initialiser l'objet à passer dans les paramètres de la recherche
			Object.entries(commande).forEach(function([key, value]) {
				if (key.startsWith('option-ut-') && (value !== '' && value != null && value > 0)) {
					quantitesOptions[key] = value;
				}
			});

			let rechercheCreneaux = {
				"productKey": produitKey,
				"quantitesUnitesTravail": quantitesUnitesTravail,
				"quantitesOptions": quantitesOptions,
				"adresse": commande.adresse,
				"codePostal": commande.commune.codePostal,
				"commune": commande.commune.nomCommune
			};

			let res = await post('/intervention/recherche-creneaux', rechercheCreneaux);

			if (response.ok) {

				let validationRecherche = ServiceValidation.validateRechercheCreneaux(res);
				setValidationAdresseCreneau(validationRecherche);

				if (validationRecherche.isValid) {

					setCreneauxList(res.data.liste.sort((a, b) => a.tarif > b.tarif ? 1 : -1));
					setCreneauxBestPrice(res.data.meilleurTarif);
					setCreneauxBestDate(res.data.meilleureDate);
					setDisplayCreneaux(true);
					setIsSortedDates(false);
					setIsSortedTarifs(true);
				}
				else {
					setCreneauxList(null);
					setCreneauxBestPrice(null);
					setCreneauxBestDate(null);
					setDisplayCreneaux(false);
				}
			}
			else {
				setCreneauxList(null);
				setCreneauxBestPrice(null);
				setCreneauxBestDate(null);
				setDisplayCreneaux(false);
			}
		}
		else {
			setCreneauxList(null);
			setCreneauxBestPrice(null);
			setCreneauxBestDate(null);
			setDisplayCreneaux(false);
		}
	}

	const handleFinalize = async () => {

		let validation = ServiceValidation.validateEtapeDevisPaiement(commande);
		let test = false;
		setValidationDevisPaiement(validation);

		if (validation.isValid) {

			// Verification si mode de test
			if (commande.contactEmail === "test@gmail.com")
				test = true;

			// Action pour le tracker google
			// Initialisation du datalayer
			window.dataLayer = window.dataLayer || [];
			// Initialisation du datalayer
			window.dataLayer.push({ 'total_amount': commande.tarif.montantTotal });

			// Initialisation du message de chargement
			setLoadingText("Enregistrement de votre réservation et préparation du paiement en ligne ...");
			setLoadingTextComp(undefined);

			let synchroteamJobId = commande.synchroteamJobId ? commande.synchroteamJobId : null;
			let isEntreprise = commande.clientType === 'particulier' ? false : true;

			let quantitesUnitesTravail = {};
			// Parcours de l'objet commande context pour récupérer le détail des quantités d'unités de travail
			Object.entries(commande).forEach(function([key, value]) {
				if (key.startsWith('ut-') && (value !== '' && value != null && value > 0)) {
					quantitesUnitesTravail[key] = value;
				}
			});

			let quantitesOptions = {};
			// Parcours de l'objet commande context pour récupérer le détail des quantités d'options
			Object.entries(commande).forEach(function([key, value]) {
				if (key.startsWith('option-ut-') && (value !== '' && value != null && value > 0)) {
					quantitesOptions[key] = value;
				}
			});

			let commandeDetails = {
				"synchroteamJobId": synchroteamJobId,
				"prestationKey": prestationKey,
				"produitKey": produitKey,
				"quantitesUnitesTravail": quantitesUnitesTravail,
				"quantitesOptions": quantitesOptions,
				"quantiteTotale": commande.quantiteTotale,
				"quantiteTotaleOptions": commande.quantiteTotaleOptions,
				"equipementMarque": commande.equipementMarque,
				"equipementModele": commande.equipementModele,
				"problemeDescription": commande.problemeDescription,
				"montantOptions": commande.montantOptions,
				"montantInitial": commande.montantInitial,
				"typePaiement": commande.typePaiement,
				"tarif": commande.tarif,
				"codePromo": commande.codePromo,
				"isPlus2ans": commande.isPlus2ans,
				"duree": commande.dureeTotale,
				"dateIntervention": commande.creneau.date,
				"heureIntervention": commande.creneau.heure,
				"heureRdvDebut": commande.creneau.heureRdvDebut,
				"heureRdvFin": commande.creneau.heureRdvFin,
				"intervenant": commande.creneau.intervenant.id,
				"adresse": commande.adresse,
				"adresseComplement": commande.adresseComplement,
				"codePostal": commande.commune.codePostal,
				"commune": commande.commune.nomCommune,
				"contactCivilite": commande.contactCivilite,
				"contactNom": commande.contactNom,
				"contactPrenom": commande.contactPrenom,
				"contactEmail": commande.contactEmail,
				"contactTelephoneMobile": commande.contactTelephoneMobile,
				"contactTelephoneFixe": commande.contactTelephoneFixe,
				"hasAdresseFacturation": commande.hasAdresseFacturation,
				"adresseFacturation": commande.hasAdresseFacturation ? commande.adresseFacturation : '',
				"adresseComplementFacturation": commande.hasAdresseFacturation ? commande.adresseComplementFacturation : '',
				"codePostalFacturation": commande.hasAdresseFacturation ? commande.communeFacturation.codePostal : '',
				"communeFacturation": commande.hasAdresseFacturation ? commande.communeFacturation.nomCommune : '',
				"isEntreprise": isEntreprise,
				"clientNomEntreprise": commande.clientNomEntreprise,
				"clientIdentifiantEntreprise": commande.clientIdentifiantEntreprise
			};

			// Log des détails de la commande
			if (process.env.REACT_APP_ENV !== 'PRD') {
				console.log(commandeDetails);
			}

			let resCommandeDetails = {};
			// Si il s'agit d'un test on n'effectue pas la creation de la commande
			if (!test)
				resCommandeDetails = await post('/commande/creation-commande', commandeDetails);
			else {
				// Mise à jour des attributs de la commande pour simuler son execution
				commandeDetails.synchroteamCustomerId = 0;
				commandeDetails.synchroteamJobId = "test";
				commandeDetails.stripeCustomerId = "test";
				commandeDetails.stripeInvoiceId = "test";
				commandeDetails.stripePaymentIntentId = "test";
				commandeDetails.stripePaymentIntentSecret = "test";

				resCommandeDetails.data = commandeDetails;
			}

			// Log des détails du résultat de la création de la commande
			if (process.env.REACT_APP_ENV !== 'PRD') {
				console.log(resCommandeDetails.data);
				console.log(response);
			}

			if (response.ok || test) {

				// Enregistrement du résultat dans les cookies
				// Afin de pouvoir les rafficher dans une autre page
				Cookies.set("commandeDetails", JSON.stringify(resCommandeDetails.data));
				Cookies.set("commandeCreneau", JSON.stringify(commande.creneau));
				Cookies.set("commandeCommune", JSON.stringify(commande.commune));

				// Affichage du module de paiment pour les cas PAIEMENT_IMMEDIAT et PAIEMENT_2FOIS
				if (!test && commande.typePaiement !== 'PAIEMENT_INTERVENTION') {
					setClientSecret(resCommandeDetails.data.stripePaymentIntentSecret);
					setDisplayPaiement(true);
					// Si tout est ok au moment du paiement la redirection est automatique
				}
				else { // Passage au step suivant via une redirection pour le cas de test
					// redirection vers l'etape commande-confirmation
					window.location.href = process.env.REACT_APP_CATALOGUE_URL + '/' + prestationKey + '/' + produitKey + '/commande-confirmation/';
				}

			}// Fin si creation-commande OK
		}// Fin si validation formulaire OK
	}// Fin fonction handleFinalize

	const handleSortTarifs = () => {
		const creneauxTarifs = [].concat(creneauxList).sort((a, b) => a.tarif > b.tarif ? 1 : -1);
		setCreneauxList(creneauxTarifs);
		setIsSortedTarifs(true);
		setIsSortedDates(false);
	}

	const handleSortDates = () => {
		const creneauxDates = [].concat(creneauxList).sort((a, b) => a.date > b.date ? 1 : -1);
		setCreneauxList(creneauxDates);
		setIsSortedDates(true);
		setIsSortedTarifs(false);
	}

	const handleSelectCreneau = (creneau) => {
		dispatch(changeValeur('creneau', creneau));
		
		if(commande.produit.paiementIntervention)
			dispatch(changeValeur('typePaiement', 'PAIEMENT_INTERVENTION'));
		else
			dispatch(changeValeur('typePaiement', 'PAIEMENT_IMMEDIAT'));

		dispatch(changeValeur('tarif', creneau.tarifs.PAIEMENT_IMMEDIAT));
		dispatch(changeValeur('montantInitial', creneau.tarifs.PAIEMENT_IMMEDIAT.montantTotal));
		setActiveStep(2);
		stepperRef.current.scrollIntoView();
	}

	// Gère l'affichage du bouton de retour à la liste des offres'
	let textList = "Retour aux offres"
	let displayList = false;

	// Gère l'affichage du bouton back'
	let textBack = "Etape précédente";
	let displayBack = false;

	// Gère l'affichage du bouton next'
	let textNext = "Etape suivante";
	let displayNext = false;

	// Gère l'affichage du bouton Passer au paiement / Valider ma réservation
	let textFinalize = commande.typePaiement !== 'PAIEMENT_INTERVENTION' ? "Passer au paiement" : "Valider ma réservation";
	let displayFinalize = false;

	// Gère l'affichage du bouton Rechercher Créneaux
	let textSearch = "Rechercher créneaux"
	let displaySearch = false;

	// Etape de selection des détails
	if (activeStep === 0) {
		displayList = true;
		displayNext = true;
	}
	// Etape de recherche et de selection du creneau 
	else if (activeStep === 1) {
		displayBack = true;
		displaySearch = true;
	}
	// Etape de Finalisation et paiement
	else if (activeStep === 2) {
		displayBack = true;
		displayFinalize = true;
	}
	// Etape de Confirmation de commande ou Cas d'erreur
	else {
		displayList = true;
	}

	return (

		<>
			{
				// Affichage du loader par dessus l'ensemble de la page
				loading && <Loader text={loadingText} textComp={loadingTextComp} />
			}

			<Stepper
				ref={stepperRef}
				alternativeLabel
				activeStep={activeStep}
				sx={{ pb: 4 }}>
				{steps.map((label) => (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
				))}
			</Stepper>

			<>
				{
					activeStep === 0 && <EtapeProduitDetails
						props={{ validation: validationProduitDetails }} />
				}
				{
					activeStep === 1 && <EtapeAdresseCreneau
						props={{
							validation: validationAdresseCreneau,
							displayCreneaux: displayCreneaux,
							creneauxList: creneauxList,
							creneauxBestPrice: creneauxBestPrice,
							creneauxBestDate: creneauxBestDate,
							displayPrix: commande.produit.displayPrix,
							actionSelectCreneau: handleSelectCreneau,
							handleSortTarifs: handleSortTarifs,
							handleSortDates: handleSortDates,
							isSortedTarifs: isSortedTarifs,
							isSortedDates: isSortedDates,
							error: error
						}} />
				}
				{
					activeStep === 2 && <EtapeDevisPaiement
						props={{ validation: validationDevisPaiement }}
						clientType={commande.clientType}
						displayPaiement={displayPaiement} />
				}
				{
					(activeStep === 3 && finalize === 'commande-confirmation') && <EtapeConfirmation />
				}
				{
					(activeStep < 0 || activeStep > 3 || (activeStep === 3 && finalize !== 'commande-confirmation')) && <EtapeErreur />
				}

				<Grid container direction="row" justifyContent="center" align="center" py={2} pt={4}>

					{
						// Cas d'affichage du bouton de retour à la liste des offres
						displayList && (
							<Grid item xs={6} sm={4} md={4} lg={3} xl={3} sx={{ display: 'flex', justifyContent: 'center' }}>
								<ButtonNav props={{ text: textList, url: process.env.REACT_APP_CATALOGUE_URL + '/' + prestationKey + '/' }} />
							</Grid>
						)
					}
					{
						// Cas d'affichage du bouton étape précédente
						displayBack && !displayPaiement && (
							<Grid item xs={6} sm={4} md={4} lg={3} xl={3} sx={{ display: 'flex', justifyContent: 'center' }}>
								<ButtonNav props={{ text: textBack }} action={handleBack} />
							</Grid>
						)
					}
					{
						// Cas d'affichage du bouton étape suivante
						displayNext && (
							<Grid item xs={6} sm={4} md={4} lg={3} xl={3} sx={{ display: 'flex', justifyContent: 'center' }}>
								<ButtonNav props={{ text: textNext, tracking: 'next-step-btn' }} action={handleNext} />
							</Grid>
						)
					}
					{
						// Cas d'affichage du bouton rechercher créneaux
						displaySearch && (
							<Grid item xs={6} sm={4} md={4} lg={3} xl={3} sx={{ display: 'flex', justifyContent: 'center' }}>
								<ButtonNav props={{ text: textSearch, tracking: 'time-slot-btn' }} action={handleSearch} />
							</Grid>
						)
					}
					{
						// Cas d'affichage du bouton Passer au Paiement
						displayFinalize && !displayPaiement && (
							<Grid item xs={6} sm={4} md={4} lg={3} xl={3} sx={{ display: 'flex', justifyContent: 'center' }}>
								<ButtonNav props={{ text: textFinalize, tracking: 'payment-begin-btn' }} action={handleFinalize} />
							</Grid>
						)
					}

					{
						// Si on test à l'étape de validation de la réservation
						// On affiche les garanties et les moyens de paiement
						activeStep <= 2 &&
						<>
							<Garanties />
							<ModePaiement />
						</>
					}

				</Grid>

				{
					activeStep === 2 && displayPaiement && (
						<PaiementStripe clientSecret={clientSecret} />
					)
				}
			</>
		</>
	);
}